import type {
  CropRegion,
  Dimensions,
  ExifOrientation,
  ImagePreprocessErrorCode,
  OutputLadderStep,
  PixelSample,
  SniffedImage,
} from '../types';

export type ImageCodecCapabilities = {
  /** Whether `decode` already applied EXIF orientation (probed once per session). */
  readonly appliesExifOrientation: boolean;
};

/**
 * Raised by a codec stage; the pipeline turns it into a user-facing
 * `ImagePreprocessError` with the destination's message context.
 */
export class ImageCodecFailure extends Error {
  override readonly name = 'ImageCodecFailure';

  constructor(
    readonly code: Extract<ImagePreprocessErrorCode, 'decode-failed' | 'encode-failed'>,
    message: string,
    options?: {readonly cause?: unknown},
  ) {
    super(message, options);
  }
}

/**
 * The seam between the pure pipeline and the browser: every method is a plain
 * pixel operation so tests can drive the pipeline with a fake.
 */
export interface ImageCodec<TImage extends Dimensions> {
  decode(blob: Blob, sniff: SniffedImage): Promise<TImage>;
  transform(image: TImage, orientation: ExifOrientation): Promise<TImage>;
  resize(image: TImage, source: CropRegion, target: Dimensions): Promise<TImage>;
  samplePixels(image: TImage, tiles: readonly CropRegion[]): readonly PixelSample[];
  encode(image: TImage, step: OutputLadderStep): Promise<Blob>;
  close(image: TImage): void;
}
